import { format, isValid } from 'date-fns';
import type { StorageFileItem } from '@/lib/api/types/storage.types';
import type { MediaAsset, MediaAssetType } from '../types/media-library.types';

export const formatFileSize = (bytes?: number): string => {
  if (typeof bytes !== 'number' || bytes <= 0) return '0 KB';
  if (bytes < 1024 * 1024) {
    const kb = bytes / 1024;
    return `${kb.toFixed(1)} KB`;
  }
  const mb = bytes / (1024 * 1024);
  return `${mb.toFixed(1)} MB`;
};

export const extractFileType = (
  name?: string | null,
  typeString?: string | null
): string => {
  const targetName = (name || '').toLowerCase().trim();
  const targetType = (typeString || '').toLowerCase().trim();

  if (targetName.endsWith('.png') || targetType.includes('png')) return 'PNG';
  if (
    targetName.endsWith('.jpg') ||
    targetName.endsWith('.jpeg') ||
    targetType.includes('jpeg') ||
    targetType.includes('jpg')
  ) {
    return 'JPEG';
  }
  if (targetName.endsWith('.gif') || targetType.includes('gif')) return 'GIF';
  if (targetName.endsWith('.svg') || targetType.includes('svg')) return 'SVG';
  if (targetName.endsWith('.webp') || targetType.includes('webp')) return 'WEBP';

  const extMatch = targetName.match(/\.([a-z0-9]+)$/);
  if (extMatch?.[1]) {
    return extMatch[1].toUpperCase();
  }

  return 'IMAGE';
};

export const formatAssetDate = (rawDate?: string | null): string => {
  if (!rawDate) {
    return format(new Date(), 'd MMM yyyy');
  }

  const parsed = new Date(rawDate);
  if (!isValid(parsed)) {
    return format(new Date(), 'd MMM yyyy');
  }

  return format(parsed, 'd MMM yyyy');
};

export const storageItemToMediaAsset = (item: StorageFileItem): MediaAsset => {
  const name = item.name || item.systemName || 'untitled-image';
  const type = extractFileType(name, item.typeString);
  const id =
    item.itemId ||
    `asset-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;
  const url = item.url || '';

  return {
    id,
    name,
    url,
    sizeInBytes: item.sizeInBytes || 0,
    type,
    width: item.width,
    height: item.height,
    createdAt: item.createdAt || item.createdDate,
    createdDate: item.createdDate || item.createdAt,
    raw: item,
  };
};

export const matchesTypeFilter = (
  asset: MediaAsset,
  filterType?: MediaAssetType
): boolean => {
  if (!filterType || filterType === 'ALL') return true;
  return asset.type.toUpperCase() === filterType.toUpperCase();
};
